import { FC } from "react";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  RadioButtonConfigurationOption,
} from "../../../../components/admin/formFields";
import { useTranslation } from "react-i18next";
import {
  AUDIO_ALERT_LANGUAGE_OPTIONS,
  PARKING_PHOTO_OPTIONS,
  SPEED_UNIT_OPTIONS,
} from "../../constants";
import RangeSlider from "../../components/range-slider";

interface CameraSystemSettingsProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  setFieldTouched: (field: string, isTouched: boolean) => void;
}

const CameraSystemSettings: FC<CameraSystemSettingsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handleBlur,
  setFieldTouched,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.camera_settings",
  });

  const driverIdOptions = [
    {
      value: "normal",
      title: t("driver_id_standard"),
      description:
        "Provides basic badge-in reminders and confirms when a badge has been presented",
      points: [
        "Reminder when the ignition is turned on.",
        "One additional reminder after 3 minutes if a badge has not been presented.",
        "Announces a successful badge-in when a badge is presented.",
        "Driver validation occurs within the FleetTrack system, not on the camera.",
      ],
    },
    {
      value: "enhanced",
      title: t("driver_id_enhanced"),
      description:
        "Provides persistent badge-in reminders with badge validation and verbal status prompts.",
      points: [
        "Reminder when the ignition is turned on.",
        "Repeats the reminder every 15-seconds until a valid badge is presented or the ignition is turned off.",
        "Validates the badge when it is presented.",
        "Announces “Driver badged in successfully” when the badge is valid.",
        "Announces “Badge not recognized” when the badge is invalid.",
        "If “Badge not recognized”, the driver ID flow restarts with 15-second reminders."
      ],
    },
  ];
  return (
    <>
      <div className="col-span-6 p-6 border-b">
        <h1 className="text-lg text-accent-blue-dark font-bold mb-4">
            {t("system")}
          </h1>
        <h2 className="block font-semibold text-sm leading-none tracking-normal mb-4">
          {t("volume")}
        </h2>
        <RangeSlider
          id="volume"
          min={0}
          max={5}
          step={1}
          value={values.volume}
          onChange={handleChange}
          stepLabels={["🔇", "1", "2", "3", "4", "🔊"]}
          isBottomLabel={true}
        />
      </div>
      <div
        className="col-span-6 p-6 grid grid-cols-6 gap-6"
        key={`camera-system-settings_${JSON.stringify(values)}`} // to re-render fields (esp. dropdowns) when value loads via API and changes>
      >
        <AdminFormFieldDropdown
          label={t("speed_unit")}
          id="speed_unit"
          name="speed_unit"
          value={values.speed_unit}
          options={SPEED_UNIT_OPTIONS}
          onChange={(e) => {
            setFieldValue("speed_unit", e?.value);
          }}
          onBlur={(e) => {
            setFieldTouched("speed_unit", true);
            handleBlur(e);
          }}
          error={errors.speed_unit}
          touched={touched.speed_unit}
          customWrapperClass="col-span-3"
        />
        <AdminFormFieldDropdown
          label={t("parking_photo")}
          id="parking_photo"
          name="parking_photo"
          value={values.parking_photo}
          options={PARKING_PHOTO_OPTIONS}
          onChange={(e) => {
            setFieldValue("parking_photo", e?.value);
          }}
          onBlur={(e) => {
            setFieldTouched("parking_photo", true);
            handleBlur(e);
          }}
          error={errors.parking_photo}
          touched={touched.parking_photo}
          customWrapperClass="col-span-3"
        />
        <AdminFormFieldDropdown
          label={t("audio_alert")}
          id="audio_alert"
          name="audio_alert"
          value={values.audio_alert}
          options={AUDIO_ALERT_LANGUAGE_OPTIONS}
          onChange={(e) => {
            setFieldValue("audio_alert", e?.value);
          }}
          onBlur={(e) => {
            setFieldTouched("audio_alert", true);
            handleBlur(e);
          }}
          error={errors.audio_alert}
          touched={touched.audio_alert}
          customWrapperClass="col-span-3"
        />
        <div className="col-span-6 grid grid-cols-8">
          <AdminFormFieldCheckbox
            customWrapperClass="col-span-4"
            label={t("live_view_audio_hint")}
            id="live_view_audio_hint"
            type="checkbox"
            name="live_view_audio_hint"
            checked={values.live_view_audio_hint}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.live_view_audio_hint}
            touched={touched.live_view_audio_hint}
          />
        </div>
        <div className="col-span-12 grid grid-cols-8">
          <AdminFormFieldCheckbox
            customWrapperClass="col-span-8"
            label={t("driver_id_login_audio_hint")}
            description={t("driver_id_login_audio_hint_description")}
            id="driver_id_login_audio_hint"
            type="checkbox"
            name="driver_id_login_audio_hint"
            checked={values.driver_id_login_audio_hint}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.driver_id_login_audio_hint}
            touched={touched.driver_id_login_audio_hint}
          />
          {values.driver_id_login_audio_hint && (
            <div className="p-3 col-span-8 bg-white flex flex-col rounded-lg shadow-md shadow-black/5 border border-black/10">
              <div className="flex flex-col gap-1">
                <span
                  className="block font-display font-semibold text-sm text-field-label-valid"
                >
                  {t("driver_id_mode")}
                </span>
                <span className="text-xs text-gray-700">
                  {t("driver_id_mode_description")}
                </span>
              </div>
              <div className="flex flex-col gap-1 mt-2 items-stretch lg:flex-row">
                {driverIdOptions.map((option) => (
                   <div key={option.value} className="flex-1 min-w-0">
                  <RadioButtonConfigurationOption
                    key={option.value}
                    {...option}
                    selected={values.driver_id_mode === option.value}
                    onChange={() => {
                      setFieldValue("driver_id_mode", option.value);
                    }}
                  />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div >
    </>
  );
};
export default CameraSystemSettings;
